import './types/core-ext';
import { createApp, type Plugin } from 'vue';
import { setupApp } from '@/setup/setup-app';
import { i18nFactory } from '@/setup/i18n';
import { routerFactory } from '@/router/router';
import { extractJwtFromUrl } from '@/boot/early-jwt-extraction';
import { store } from '@/store';
import PlayApp from '@/PlayApp.vue';
import { env } from '@/env';

import {
    auth,
    authCallbackError,
    clearAuth0LocalStorage,
    consumeQueuedRedirectTarget,
} from '@/plugins/auth';
import { isEmbedded } from '@/play-editor/frame';

// Extract JWT from URL before store initialization
// This ensures the JWT is available when feature flags are fetched during store rehydration
extractJwtFromUrl();

const app = createApp(PlayApp);

// SHAR-1243: register the Auth0 SPA plugin only for the standalone, non-embedded
if (!isEmbedded() && auth) {
    app.use(auth as unknown as Plugin);
    consumeQueuedRedirectTarget();

    // Wipe session only for a terminal Auth0 callback error captured at boot
    // (authCallbackError). Do not react to the shared auth.error ref — that is
    // set by any failed SDK call (including silent token renewal) and would
    // block normal re-login recovery.
    if (authCallbackError) {
        store.commit('auth/SESSION_UNAUTHORIZED');
        clearAuth0LocalStorage();
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
const { router, i18n } = setupApp(app, routerFactory, i18nFactory, () => store);

app.provide('appComponents', {
    ready: true,
    router,
    i18n,
    store,
});

// Expose store and other components for debugging in non-production
if (!env.PROD) {
    window.__PLAY_DEBUG__ = {
        store, router, i18n, app,
    };
}

router.isReady().then(() => {
    app.mount('#app');
});
